import 'normalize.css';
// include fullcalendar css
// currently in app.tsx, but should be in components
// using fullcalendar when nextjs updated to v10
// see https://nextjs.org/blog/next-10#importing-css-from-third-party-react-components
import '@fullcalendar/common/main.css';
import '@fullcalendar/daygrid/main.css';
import '@fullcalendar/timegrid/main.css';
import '@fullcalendar/list/main.css';
import './_app.scss';

import { NextPageContext } from 'next';
import withRedux from 'next-redux-wrapper';
import App, { AppContext, AppInitialProps, AppProps } from 'next/app';
import getConfig from 'next/config';
import * as NProgress from 'nprogress';
import * as React from 'react';
import { Provider } from 'react-redux';
import { Store } from 'redux';

import * as Sentry from '@sentry/browser';

import I18n, { detectLocaleByAcceptLanguage } from '../utils/i18n';

import * as LocaleContext from '../contexts/LocaleContext';
import * as UserContext from '../contexts/UserContext';
import * as ViewDeviceContext from '../contexts/ViewDeviceContext';
import * as errors from '../infrastructure/repositories/exceptions/errors';
import implSessionRepository from '../infrastructure/repositories/SessionRepository';
import makeStore from '../redux';
import { getCookie, setCookie } from '../utils/cookie';
import * as gtag from '../utils/gtag';
import {
  setCoubicLocaleRequestHeader,
  setUaType,
} from '../utils/secured-axios';
import ErrorComponent, { ErrorStatusCode } from './_error';

Sentry.init({
  dsn: getConfig().publicRuntimeConfig.sentryDsn,
  environment: getConfig().publicRuntimeConfig.coubicEnv,
});

interface ReduxProps {
  store: Store;
  isServer: boolean;
}

interface ErrorProps {
  statusCode: ErrorStatusCode;
  message: string;
}

interface Props extends ReduxProps, AppProps {
  userContextProps: UserContext.InputContext;
  err?: ErrorProps;
  locale: string;
  viewDeviceContextProps: ViewDeviceContext.ViewDeviceContextProps;
}

// @see 'next-redux-wrapper' StoreCreatorOptions interface
interface ReduxContext {
  isServer: boolean;
}

export interface PageContext extends NextPageContext, ReduxContext {
  session: string;
  token: string;
  requestId: string;
}

export interface NextAppContext extends AppContext {
  ctx: PageContext;
}

class CbcApp extends App<Props> {
  public static async getInitialProps({
    Component,
    ctx,
  }: NextAppContext & AppInitialProps) {
    // Set language from cookie
    const { isServer, req, query } = ctx;

    const locale: string =
      query.hl ||
      getCookie('hl', req) ||
      (req && detectLocaleByAcceptLanguage(req.headers['accept-language'])) ||
      '';

    if (I18n.isAvailableCoubicLocale(locale)) {
      setCoubicLocaleRequestHeader(locale);
    }

    // Set Uatype to Secure Axios.
    const uatype = CbcApp.detectUaType(req);
    setUaType(uatype);

    // Handle props of a child page and context.
    let pageProps = {};
    let userContextProps: UserContext.InputContext = {
      coubicUAType: '',
      requestId: '',
      isServer,
      host: '',
      session: '',
    };
    let formatErr: ErrorProps | undefined;

    ctx.token = req ? getCookie('token', req) : '';
    ctx.session = req ? getCookie('cb', req) : '';

    const requestId = req ? (req as any).id : '';
    ctx.requestId = requestId;

    try {
      pageProps = Component.getInitialProps
        ? await Component.getInitialProps(ctx)
        : {};
      userContextProps = await UserContext.makeInputContext(
        isServer,
        requestId,
        req,
      );
    } catch (unhandledErr) {
      formatErr = CbcApp.formatException(unhandledErr);
    }

    if (ctx.res) {
      // Set _userId for express server log output
      const userPublicId = userContextProps.user
        ? userContextProps.user.publicId
        : '';
      if (getCookie('_userId', req) !== userPublicId) {
        setCookie('_userId', userPublicId, ctx.res);
      }
      // Set statuscode to server response if an error occured
      if (formatErr) {
        ctx.res.statusCode = formatErr.statusCode;
      }

      // sessionが空の場合、発行してもらう
      if (ctx.session === '' || ctx.session === undefined) {
        ctx.session = await CbcApp.getTmpSession();
        setCookie('cb', ctx.session, ctx.res);
      }
    }

    return { pageProps, userContextProps, err: formatErr, locale };
  }

  // tslint:disable-next-line: no-any
  private static detectUaType(req: any): string {
    return req && req.headers
      ? req.headers.Uatype || getCookie('uatype', req) || ''
      : '';
  }

  private static formatException(err: Error): ErrorProps {
    if (err instanceof errors.AuthenticationFailed) {
      return { statusCode: 401 as ErrorStatusCode, message: err.message };
    }

    if (err instanceof errors.NotFound) {
      return { statusCode: 404 as ErrorStatusCode, message: err.message };
    }

    if (err instanceof errors.ServiceTemporaryUnavailable) {
      return { statusCode: 503 as ErrorStatusCode, message: err.message };
    }

    Sentry.captureException(err);

    return {
      statusCode: 500 as ErrorStatusCode,
      message: err.message,
    };
  }

  private static async getTmpSession(): Promise<string> {
    const res = await implSessionRepository().Create();
    return res.sessionToken;
  }

  public postPageViewToGA(url?: string) {
    try {
      gtag.pageview(url);
    } catch (err) {
      if (getConfig().publicRuntimeConfig.coubicEnv === 'production') {
        Sentry.captureException(err);
      }
    }
  }

  // tslint:disable-next-line: no-any
  public setIndicator(router: any) {
    router.events.on('routeChangeStart', () => NProgress.start());
    router.events.on('routeChangeComplete', () => NProgress.done());
    router.events.on('routeChangeError', () => NProgress.done());
  }

  public async componentDidMount() {
    this.postPageViewToGA(this.props.router.asPath);
    this.setIndicator(this.props.router);
    // nodeサーバーでは動作しないのでstripeの初期化をここでやる
    const { publicRuntimeConfig } = getConfig();
    Stripe.setPublishableKey(publicRuntimeConfig.stripePublicKey);
  }

  public async componentDidUpdate() {
    this.postPageViewToGA(this.props.router.asPath);
  }

  public async componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    Sentry.withScope(scope => {
      Object.keys(errorInfo).forEach(key => {
        scope.setExtra(key, errorInfo[key]);
      });
      Sentry.captureException(error);
    });
  }

  public render() {
    const {
      Component,
      pageProps,
      userContextProps,
      store,
      viewDeviceContextProps,
    } = this.props;

    return (
      <ViewDeviceContext.ViewDeviceContextProvider {...viewDeviceContextProps}>
        <LocaleContext.LocaleContextProvider locale={this.props.locale}>
          <UserContext.UserContextProvider {...userContextProps}>
            <Provider store={store}>
              <>
                {this.props.err && (
                  <ErrorComponent statusCode={this.props.err.statusCode} />
                )}
                {!this.props.err && <Component {...pageProps} />}
              </>
            </Provider>
          </UserContext.UserContextProvider>
        </LocaleContext.LocaleContextProvider>
      </ViewDeviceContext.ViewDeviceContextProvider>
    );
  }
}

export default withRedux(makeStore)(CbcApp);
